import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { api } from '../../lib/api-client'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'
import { Separator } from '../ui/separator'
import { DebugFooter } from './DebugFooter'
import {
  AcademicCapIcon,
  BanknotesIcon,
  BriefcaseIcon,
  Bars3Icon,
  BookOpenIcon,
  BuildingOffice2Icon,
  BuildingStorefrontIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  CpuChipIcon,
  CreditCardIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  MegaphoneIcon,
  PencilSquareIcon,
  PuzzlePieceIcon,
  QuestionMarkCircleIcon,
  RectangleGroupIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  TrophyIcon,
  UserGroupIcon,
  UserPlusIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

interface ShellNavItem {
  label: string
  path: string
  icon?: ReactNode
  hidden?: boolean
}

interface ShellClusterRef {
  id: string
  label: string
  basePath: string
}

interface AdminShellProps {
  children: ReactNode
  /** Big-group name (e.g. "Cộng đồng"), shown above the cluster switcher. */
  groupLabel: string
  /** Active cluster name. */
  clusterLabel: string
  /** The active cluster's pages — the only links shown in the left-panel. */
  items: ShellNavItem[]
  /** The 4 clusters in the current group, for the compact in-group switcher. */
  siblings: ShellClusterRef[]
}

const NAV_ICONS: Record<string, typeof Squares2X2Icon> = {
  Overview: Squares2X2Icon, Members: UsersIcon, 'Member Intelligence': CpuChipIcon,
  'Membership Activation': UserPlusIcon, Mentors: AcademicCapIcon, Growth: ChartBarIcon,
  Brands: BuildingStorefrontIcon, Schools: BuildingOffice2Icon, Roles: ShieldCheckIcon,
  'Bank Accounts': BanknotesIcon, 'FAQ Management': QuestionMarkCircleIcon,
  'Trial Members': UserGroupIcon, 'Lead Scoring': ChartBarIcon, Enrollments: ClipboardDocumentCheckIcon,
  'Email Logs': EnvelopeIcon, 'Email Scores': ChartBarIcon, 'Email Catalog': DocumentTextIcon,
  'Book Engine': CpuChipIcon, 'Create Book': BookOpenIcon, 'Book Packages': RectangleGroupIcon,
  'Book Quizzes': QuestionMarkCircleIcon, 'Review Studio': ClipboardDocumentCheckIcon,
  Courses: BookOpenIcon, Workshops: AcademicCapIcon, Docs: DocumentTextIcon,
  'All Content Audits': ShieldCheckIcon, Feedback: ChatBubbleLeftRightIcon,
  'All Classes': UserGroupIcon, Schedule: CalendarDaysIcon, Certificates: TrophyIcon,
  Frameworks: RectangleGroupIcon, Quizzes: QuestionMarkCircleIcon, Challenges: PuzzlePieceIcon,
  Payments: CreditCardIcon, Issues: ExclamationTriangleIcon, 'Course Marketing': MegaphoneIcon,
  Testimonials: ChatBubbleLeftRightIcon, Events: CalendarDaysIcon, 'Event Series': RectangleGroupIcon,
  Groups: UserGroupIcon, Content: DocumentTextIcon, Plans: ClipboardDocumentCheckIcon,
  Rules: ShieldCheckIcon, Reports: ChartBarIcon, Photos: RectangleGroupIcon,
  'Feature Portfolio': Squares2X2Icon, 'Content Factory': WrenchScrewdriverIcon,
  'Style Guide': PencilSquareIcon, Recruitment: BriefcaseIcon,
}

/** True when `pathname` is at `base` or a descendant of it (segment-aware). */
function isUnder(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(base + '/')
}

export function AdminShell({ children, groupLabel, clusterLabel, items, siblings }: AdminShellProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const [isCollapsed, setIsCollapsed] = useState(() => localStorage.getItem('admin_sidebar_collapsed') === 'true')
  const [mobileOpen, setMobileOpen] = useState(false)
  // Read-only: the active school is chosen on HOME; here we only display its name.
  const schoolName = localStorage.getItem('fc_school_name') || ''

  const visibleItems = useMemo(() => items.filter(item => !item.hidden), [items])
  const currentClusterPath = useMemo(
    () => siblings.find(sibling => isUnder(location.pathname, sibling.basePath))?.basePath || '',
    [siblings, location.pathname],
  )
  // Highlight the single deepest item the URL sits under (so e.g. /content/books
  // doesn't stay lit while viewing /content/books/quizzes).
  const activeItemPath = useMemo(() => {
    let best = ''
    for (const item of visibleItems) {
      if (item.path.includes(':')) continue
      if (isUnder(location.pathname, item.path) && item.path.length > best.length) best = item.path
    }
    return best
  }, [visibleItems, location.pathname])

  useEffect(() => localStorage.setItem('admin_sidebar_collapsed', String(isCollapsed)), [isCollapsed])
  useEffect(() => { setMobileOpen(false) }, [location.pathname])

  const sidebar = (
    <>
      <div className="flex h-16 items-center gap-3 px-4">
        <Link to="/" title="Trang chủ" aria-label="Về trang chủ" className="flex min-w-0 items-center gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <CpuChipIcon className="size-5" />
          </div>
          {!isCollapsed && <div className="min-w-0">
            <p className="truncate text-sm font-semibold">Conan Admin</p>
            <p className="truncate text-sm text-muted-foreground">{schoolName || clusterLabel}</p>
          </div>}
        </Link>
        <Button variant="ghost" size="icon-sm" className="ml-auto lg:hidden" aria-label="Đóng menu" onClick={() => setMobileOpen(false)}>
          <XMarkIcon />
        </Button>
      </div>
      <Separator />

      {/* Group/cluster shown as static context only. Switching cluster, school,
          and user/logout all live on the HOME page — not inside a cluster. */}
      {!isCollapsed && <div className="px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{groupLabel}</p>
        <p className="mt-0.5 truncate text-sm font-medium text-foreground">{clusterLabel}</p>
      </div>}
      {!isCollapsed && <Separator />}

      <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-3">
        {visibleItems.map(item => {
          const active = item.path === activeItemPath
          const Icon = NAV_ICONS[item.label] || Squares2X2Icon
          return <Link key={item.path} to={item.path} title={item.label} className={cn(
            'flex h-10 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors',
            active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            isCollapsed && 'justify-center px-0'
          )}>
            <span className="shrink-0 [&_svg]:size-5">{item.icon || <Icon />}</span>
            {!isCollapsed && <span className="truncate">{item.label}</span>}
          </Link>
        })}
      </nav>
      <Separator />
      {/* Read-only: shows the logged-in name. No logout / no actions here — that lives on HOME. */}
      <UserInfo isCollapsed={isCollapsed} />
      <Separator />
      <button onClick={() => setIsCollapsed(value => !value)}
        className="hidden h-12 items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground lg:flex"
        aria-label={isCollapsed ? 'Mở rộng sidebar' : 'Thu gọn sidebar'}>
        {isCollapsed ? <ChevronRightIcon className="size-5" /> : <ChevronLeftIcon className="size-5" />}
      </button>
    </>
  )

  return <div className="admin-layout min-h-screen bg-background">
    {mobileOpen && <button aria-label="Đóng menu" className="fixed inset-0 z-40 bg-foreground/20 backdrop-blur-[1px] lg:hidden" onClick={() => setMobileOpen(false)} />}
    <aside className={cn(
      'admin-sidebar fixed inset-y-0 left-0 z-50 flex flex-col border-r bg-sidebar transition-[width,transform] duration-200 lg:sticky lg:top-0 lg:z-20 lg:h-screen lg:translate-x-0',
      isCollapsed ? 'lg:w-[72px]' : 'lg:w-[264px]',
      mobileOpen ? 'w-[288px] translate-x-0' : 'w-[288px] -translate-x-full'
    )}>{sidebar}</aside>
    <div className="min-w-0 flex-1">
      <header className="admin-mobile-header sticky top-0 z-30 flex h-14 items-center gap-3 border-b bg-background/95 px-4 backdrop-blur lg:hidden">
        <Button variant="ghost" size="icon-sm" aria-label="Mở menu" onClick={() => setMobileOpen(true)}><Bars3Icon /></Button>
        <Link to="/" aria-label="Về trang chủ" className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground"><CpuChipIcon className="size-5" /></Link>
        <div className="min-w-0"><p className="truncate text-sm font-semibold">{clusterLabel}</p></div>
      </header>
      <main className="admin-main min-w-0">{children}</main>
    </div>
    <DebugFooter />
  </div>
}

function UserInfo({ isCollapsed }: { isCollapsed: boolean }) {
  let user: { name?: string; email?: string; avatar?: string } | null = null
  try {
    const raw = localStorage.getItem('fc_admin_auth')
    if (raw) user = JSON.parse(raw)
  } catch { /* invalid legacy local state */ }
  if (!user) return null
  return <div className={cn('flex min-h-16 items-center gap-3 p-3', isCollapsed && 'justify-center')}>
    {user.avatar
      ? <img src={user.avatar} alt="" className="size-9 shrink-0 rounded-full object-cover" />
      : <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold">{(user.name || user.email || '?').charAt(0).toUpperCase()}</div>}
    {!isCollapsed && <div className="min-w-0">
      <p className="truncate text-sm font-medium">{user.name || 'Admin'}</p>
      <p className="truncate text-sm text-muted-foreground">{user.email}</p>
    </div>}
  </div>
}
